/**
 * Custom element <flip-card> : acteur (état + cycle de vie) qui anime le
 * passage d'une valeur à la suivante. Le parent pousse la valeur via
 * `data-value` ; la carte déclenche le flip.
 * Transcription de dpc-timer : src/components/atoms/FlipCard.client.ts
 * (styles : src/countdown.css)
 */

const FLIP_DURATION_MS = 600;

class FlipCard extends HTMLElement {
  static observedAttributes = ["data-value"];

  #staticTop = null;
  #staticBottom = null;
  #leafTop = null;
  #leafBottom = null;
  #current = "";
  #timer = 0;
  #reduceMotion = false;

  connectedCallback() {
    this.#staticTop = this.querySelector("[data-static-top]");
    this.#staticBottom = this.querySelector("[data-static-bottom]");
    this.#leafTop = this.querySelector("[data-leaf-top]");
    this.#leafBottom = this.querySelector("[data-leaf-bottom]");
    this.#reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Valeur éventuellement poussée avant la définition de l'élément : on
    // l'affiche telle quelle, sans flip.
    this.#current = this.dataset.value ?? "";
    if (this.#staticTop) this.#staticTop.textContent = this.#current;
    if (this.#staticBottom) this.#staticBottom.textContent = this.#current;
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
  }

  attributeChangedCallback(name, _old, value) {
    if (name !== "data-value" || value === null || value === this.#current) return;
    this.#flipTo(value);
  }

  #flipTo(next) {
    const staticTop = this.#staticTop;
    const staticBottom = this.#staticBottom;
    const leafTop = this.#leafTop;
    const leafBottom = this.#leafBottom;
    if (!staticTop || !staticBottom || !leafTop || !leafBottom) {
      this.#current = next;
      return;
    }

    const prev = this.#current;
    this.#current = next;

    // Première valeur (carte rendue vide) : rien à replier, pas de flip depuis le vide
    if (this.#reduceMotion || prev === "") {
      staticTop.textContent = next;
      staticBottom.textContent = next;
      return;
    }

    window.clearTimeout(this.#timer);
    leafTop.textContent = prev; // feuille haute (ancienne) qui se replie
    staticTop.textContent = next; // dessous : nouveau haut, révélé par le pli
    leafBottom.textContent = next; // feuille basse (nouvelle) qui se déplie
    staticBottom.textContent = prev; // bas inchangé jusqu'à la fin du pli

    this.classList.remove("is-flipping");
    this.getBoundingClientRect(); // force un reflow pour rejouer l'animation
    this.classList.add("is-flipping");

    this.#timer = window.setTimeout(() => {
      staticBottom.textContent = next;
      this.classList.remove("is-flipping");
    }, FLIP_DURATION_MS);
  }
}

if (!customElements.get("flip-card")) {
  customElements.define("flip-card", FlipCard);
}
